import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ArrowRight } from "lucide-react";
import { SITE, SERVICES } from "@/config/site";
import { trackEvent } from "@/lib/analytics";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/zones-intervention", label: "Zones d'intervention" },
  { to: "/conseils", label: "Conseils" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const servicesRef = useRef(null);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!servicesOpen) return;
    const onDocClick = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setServicesOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [servicesOpen]);

  const onCall = () => trackEvent("click_phone", { location: "header" });

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-[60] bg-white/95 backdrop-blur-xl transition-shadow ${
        scrolled ? "shadow-[0_4px_30px_rgba(0,0,0,0.08)]" : "border-b border-gray-100"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-[72px] flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0" aria-label="FRELONS50 accueil">
          <img src="/images/logo.png" alt="" aria-hidden="true" width="44" height="44" className="w-10 h-10 object-contain" />
          <span className="font-display font-extrabold text-xl tracking-tight text-brand-black">
            FRELONS<span className="text-brand-gold">50</span><sup className="text-[0.55em] font-bold text-brand-text ml-0.5">™</sup>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
          <NavItem to="/">Accueil</NavItem>

          {/* Dropdown nuisibles (clic + clavier, fermeture au clic extérieur / Échap) */}
          <div className="relative" ref={servicesRef}>
            <button
              data-testid="nav-services-toggle"
              onClick={() => setServicesOpen((v) => !v)}
              className="px-3 py-2 text-sm font-semibold text-brand-black/80 hover:text-brand-black inline-flex items-center gap-1 rounded-lg"
              aria-expanded={servicesOpen}
              aria-haspopup="true"
            >
              Nuisibles <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div className="absolute left-0 top-full pt-2 w-72" data-testid="nav-services-menu">
                <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-gray-100 p-2">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl hover:bg-brand-gray transition-colors group"
                    >
                      <span className="text-sm font-semibold text-brand-black">{s.nav}</span>
                      <ArrowRight className="w-4 h-4 text-brand-text opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {NAV.slice(1).map((n) => (
            <NavItem key={n.to} to={n.to}>{n.label}</NavItem>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <a
            href={SITE.phoneHref}
            onClick={onCall}
            data-testid="header-call-btn"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-brand-black text-brand-black font-bold text-sm hover:bg-brand-black hover:text-white transition-colors"
          >
            <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="header-intervention-btn"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-gold text-brand-black font-bold text-sm hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-8px_rgba(245,180,0,0.7)] transition-all"
          >
            Demander une intervention
          </Link>

          {/* Mobile call icon */}
          <a
            href={SITE.phoneHref}
            onClick={onCall}
            data-testid="header-call-icon"
            aria-label={`Appeler le ${SITE.phoneDisplay}`}
            className="sm:hidden inline-flex items-center justify-center w-11 h-11 rounded-full bg-brand-gold text-brand-black"
          >
            <Phone className="w-5 h-5" />
          </a>

          {/* Hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full bg-brand-black text-white"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-100 max-h-[calc(100vh-72px)] overflow-y-auto">
          <nav className="px-4 py-4 flex flex-col" aria-label="Navigation mobile">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                className="py-3 px-3 rounded-xl text-base font-semibold text-brand-black hover:bg-brand-gray"
              >
                {n.label}
              </Link>
            ))}
            <div className="mt-2 mb-1 px-3 text-xs uppercase tracking-widest font-bold text-brand-text">Nuisibles</div>
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`mobile-service-${s.slug}`}
                className="py-2.5 px-3 rounded-xl text-sm font-medium text-brand-text hover:bg-brand-gray"
              >
                {s.nav}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-intervention-btn"
              className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-brand-gold text-brand-black font-bold"
            >
              Demander une intervention
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

const NavItem = ({ to, children }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${
        isActive ? "text-brand-black" : "text-brand-black/70 hover:text-brand-black"
      }`
    }
  >
    {children}
  </NavLink>
);
